/**
 * React context that provides the Remotion Editor API service
 * with Obelisk authentication (Clerk tokens) to all editor components.
 *
 * Wrap the <Editor /> component with <RemotionApiProvider> so that
 * upload, render, progress, and caption calls go through the
 * authenticated Obelisk backend instead of direct fetch('/api/...').
 */

import { createContext, useContext, useMemo } from 'react';
import { useAuthenticatedApi } from '@/contexts/AuthenticatedApiContext';
import {
  createAuthenticatedRemotionEditorApi,
  type RemotionEditorApi,
} from '@/lib/services/remotion-editor';

export type { RemotionEditorApi };

const RemotionApiContext = createContext<RemotionEditorApi | null>(null);

export function RemotionApiProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const apiClient = useAuthenticatedApi();

  const remotionApi = useMemo(
    () => createAuthenticatedRemotionEditorApi(apiClient),
    [apiClient],
  );

  return (
    <RemotionApiContext.Provider value={remotionApi}>
      {children}
    </RemotionApiContext.Provider>
  );
}

/**
 * Hook to access the Remotion Editor API from within the editor tree.
 *
 * Must be used inside a <RemotionApiProvider>.
 */
export function useRemotionApi(): RemotionEditorApi {
  const ctx = useContext(RemotionApiContext);
  if (!ctx) {
    throw new Error(
      'useRemotionApi must be used within a <RemotionApiProvider>',
    );
  }
  return ctx;
}

